import { useState } from 'react';
import { PetAvatar } from '@/components/PetAvatar';
import { supabase } from '@/lib/supabase';

export default function WelcomeBack({ onDifferentAccount }) {
  const [loading, setLoading] = useState(false);

  const petType     = localStorage.getItem('you_pet_type') || 'panda';
  const petName     = localStorage.getItem('you_pet_name') || 'Your pet';
  const displayName = localStorage.getItem('you_display_name') || 'friend';
  const googleAvatar = localStorage.getItem('you_google_avatar') || '';
  const streak  = parseInt(localStorage.getItem('you_streak') || '0');
  const xp      = parseInt(localStorage.getItem('you_total_xp') || '0');
  const level   = parseInt(localStorage.getItem('you_current_level') || '1');
  const stories = parseInt(localStorage.getItem('you_stories_generated') || '0');

  const handleGoogle = async () => {
    setLoading(true);
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  };

  const STATS = [
    { icon: '🔥', value: streak,  label: 'Streak'  },
    { icon: '⭐', value: xp,      label: 'XP'      },
    { icon: '🐼', value: level,   label: 'Level'   },
    { icon: '📖', value: stories, label: 'Stories' },
  ];

  return (
    <div style={{
      background: '#FFFFFF',
      padding: 24,
      maxWidth: 720,
      margin: '0 auto',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}>

      {/* YOU Logo */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <svg viewBox="0 0 40 44" width={32} height={36}>
          <path d="M20 2L36 10V28C36 34 28 40 20 43C12 40 4 34 4 28V10L20 2Z"
                fill="#448AFF" stroke="#3370CC" strokeWidth="0.5"/>
          <rect x="12" y="14" width="16" height="12" rx="2" fill="#fff" opacity="0.9"/>
          <path d="M15 18H25M15 21H22M15 24H20" stroke="#E87A2E"
                stroke="#448AFF" strokeWidth="1" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Pet + Avatar Row */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
        {googleAvatar ? (
          <img
            src={googleAvatar}
            alt="Profile photo"
            style={{ width: 48, height: 48, borderRadius: '50%', border: '2px solid #448AFF', marginBottom: 8 }}
          />
        ) : null}
        <PetAvatar size={80} state="happy" petType={petType} />
        <p style={{ fontSize: 11, color: '#E6A800', textAlign: 'center', margin: '8px 0 0 0' }}>
          {petName} missed you! 🎉
        </p>
      </div>

      {/* Heading */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <p style={{ fontSize: 26, fontFamily: "'Patrick Hand', cursive", color: '#1A1A2E', margin: 0 }}>
          Welcome back, {displayName}!
        </p>
        <p style={{ fontSize: 10, color: '#666666', marginTop: 4, marginBottom: 0 }}>
          Your progress is safe 🔒
        </p>
      </div>

      {/* Pet Speech Bubble */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        textAlign: 'center',
        border: '1.5px dashed #448AFF',
      }}>
        <p style={{ fontSize: 16, color: '#555555', lineHeight: 1.5, margin: 0 }}>
          I kept all your XP and streak safe!<br />
          Just tap below to continue. 🐯
        </p>
      </div>

      {/* Progress Card */}
      <div style={{ background: '#FFFFFF', borderRadius: 4, padding: 12, marginBottom: 16, border: '1.5px dashed #CCCCCC', borderLeft: '3px solid #FFD740' }}>
        <p style={{ fontSize: 10, color: '#E6A800', fontWeight: 600, marginBottom: 10, margin: '0 0 10px 0' }}>
          Your progress is safe:
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          {STATS.map(({ icon, value, label }) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#448AFF' }}>
                {icon} {value}
              </div>
              <div style={{ fontSize: 10, color: '#666666', marginTop: 2 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Google Button */}
      <button
        onClick={handleGoogle}
        disabled={loading}
        aria-label="Continue with Google"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          width: '100%',
          background: '#FFFFFF',
          border: '1.5px dashed #CCCCCC',
          borderRadius: 12,
          padding: '16px 24px',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          marginBottom: 8,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.42 3.44 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        <span style={{ color: '#333', fontSize: 16, fontWeight: 600 }}>
          {loading ? 'Signing in...' : 'Continue with Google'}
        </span>
      </button>

      {/* Different Account Link */}
      <button
        onClick={onDifferentAccount}
        aria-label="Sign in with a different account"
        style={{ textAlign: 'center', fontSize: 11, color: '#888888', cursor: 'pointer', marginTop: 12, background: 'transparent', border: 'none', width: '100%', padding: 8 }}
      >
        Sign in with a different account
      </button>

    </div>
  );
}
